import type { SymbolKey } from '@/entities/symbol';
import type { SpotHistoryTableColumnType } from '@/features/markets/types';
import { storage, StorageKeys } from '@/services/storage';

import {
  DATA_COLUMN_WIDTH,
  DATE_COLUMN_WIDTH,
  DEFAULT_VIRTUAL_RECT,
  MAX_DATA_COLUMN_WIDTH,
  MIN_DATA_COLUMN_WIDTH
} from './constants';
import type { VirtualCell } from './types';

export function getColumnCode(column: SpotHistoryTableColumnType) {
  return column.symbol?.key.Code || column.code || '';
}

export function getColumnSymbolKey(column: SpotHistoryTableColumnType): SymbolKey | null {
  if (column.symbol?.key.Code) return column.symbol.key;
  const code = getColumnCode(column);

  if (!code) return null;

  return {
    Code: code,
    PeriodType: '',
    Period: '',
    TimeRef: ''
  };
}

export function getColumnTitle(column: SpotHistoryTableColumnType) {
  return column.name || column.alias || getColumnCode(column);
}

export function clampColumnWidth(width: number) {
  const finiteWidth = Number.isFinite(width) ? width : DATA_COLUMN_WIDTH;
  return Math.min(MAX_DATA_COLUMN_WIDTH, Math.max(MIN_DATA_COLUMN_WIDTH, finiteWidth));
}

export function readStoredColumnWidths(storageKey: string): Record<string, number> {
  return storage.get(StorageKeys.SpotHistoryColumnWidths)?.[storageKey] ?? {};
}

export function writeStoredColumnWidths(storageKey: string, widths: Record<string, number>) {
  const all = storage.get(StorageKeys.SpotHistoryColumnWidths) ?? {};
  storage.set(StorageKeys.SpotHistoryColumnWidths, { ...all, [storageKey]: widths });
}

export function buildFallbackVirtualColumns(widths: number[]): VirtualCell[] {
  let start = DATE_COLUMN_WIDTH;

  return widths
    .slice(0, Math.ceil(DEFAULT_VIRTUAL_RECT.width / MIN_DATA_COLUMN_WIDTH) + 2)
    .map((width, index) => {
      const cell = {
        index: index + 1,
        size: width,
        start
      };
      start += width;
      return cell;
    });
}
